/**
 * Tiny WebAudio synth — no asset files. Default muted; the user opts in via the
 * top-bar toggle (which is a user gesture, so the AudioContext can start).
 */

const KEY = "lunyu-sfx";
let muted = true;
try {
  muted = localStorage.getItem(KEY) !== "on";
} catch {
  /* storage unavailable */
}

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (muted || typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

interface Tone {
  freq: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  slideTo?: number;
  delay?: number;
}

function blip(tones: Tone[]): void {
  const c = audio();
  if (!c) return;
  const now = c.currentTime;
  for (const t of tones) {
    const start = now + (t.delay ?? 0);
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = t.type ?? "sine";
    osc.frequency.setValueAtTime(t.freq, start);
    if (t.slideTo) osc.frequency.exponentialRampToValueAtTime(t.slideTo, start + t.dur);
    const peak = t.gain ?? 0.16;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + t.dur);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(start);
    osc.stop(start + t.dur + 0.03);
  }
}

export const Sfx = {
  play: () => blip([{ freq: 300, slideTo: 470, dur: 0.12, type: "triangle", gain: 0.11 }]),
  hit: () =>
    blip([
      { freq: 520, slideTo: 720, dur: 0.12, type: "triangle", gain: 0.16 },
      { freq: 900, dur: 0.08, type: "sine", gain: 0.09, delay: 0.08 },
    ]),
  combo: () =>
    blip([
      { freq: 660, dur: 0.1, type: "triangle", gain: 0.15 },
      { freq: 880, dur: 0.11, type: "triangle", gain: 0.15, delay: 0.07 },
      { freq: 1180, dur: 0.14, type: "sine", gain: 0.13, delay: 0.15 },
    ]),
  miss: () =>
    blip([
      { freq: 240, slideTo: 120, dur: 0.22, type: "sawtooth", gain: 0.13 },
      { freq: 95, dur: 0.18, type: "square", gain: 0.055, delay: 0.1 },
    ]),
  type: () => blip([{ freq: 840, slideTo: 760, dur: 0.028, type: "triangle", gain: 0.018 }]),
  win: () =>
    blip([
      { freq: 523, dur: 0.13, type: "triangle", gain: 0.15 },
      { freq: 659, dur: 0.13, type: "triangle", gain: 0.15, delay: 0.12 },
      { freq: 784, dur: 0.15, type: "triangle", gain: 0.15, delay: 0.24 },
      { freq: 1046, dur: 0.34, type: "sine", gain: 0.16, delay: 0.36 },
    ]),
  lose: () =>
    blip([
      { freq: 330, slideTo: 150, dur: 0.4, type: "sawtooth", gain: 0.13 },
      { freq: 150, slideTo: 90, dur: 0.5, type: "sine", gain: 0.12, delay: 0.18 },
    ]),
  click: () => blip([{ freq: 600, dur: 0.05, type: "square", gain: 0.05 }]),
};

export function isMuted(): boolean {
  return muted;
}

export function toggleMuted(): boolean {
  muted = !muted;
  try {
    localStorage.setItem(KEY, muted ? "off" : "on");
  } catch {
    /* ignore */
  }
  if (!muted) audio(); // warm up + resume under the click gesture
  return muted;
}
